import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Upload,
  FileText,
  Calculator,
  Building2,
  Layers,
  Download,
  Plus,
  Trash2,
  RefreshCw,
  FileSpreadsheet,
  Image,
  File,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Settings,
  Ruler,
  DoorOpen,
  Pipette,
  Zap,
  Droplets,
  Wind,
  TreeDeciduous,
  Shield,
  Footprints,
  Save,
  FolderOpen,
  X,
  Clock,
  MoreVertical
} from 'lucide-react';
import { 
  calculateQuantities, 
  uploadProjectFile, 
  getQuantityCategories,
  exportQuantityExcel,
  exportQuantityPdf,
  generateCadDrawings,
  analyzeAndCalculate,
  getQuantityDownloadUrl,
  listQuantityProjects,
  getQuantityProject,
  saveCurrentProject,
  deleteQuantityProject
} from '../../lib/api';

export default function QuantityCalculatorPage() {
  // Project state
  const [project, setProject] = useState({
    name: '',
    location: '',
    client: '',
    floors: 1,
    totalArea: 0,
    buildingType: 'commercial'
  });

  // Files state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [generatingCad, setGeneratingCad] = useState(false);
  const [savingProject, setSavingProject] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const fileInputRef = useRef(null);

  // Saved projects state
  const [savedProjects, setSavedProjects] = useState([]);
  const [showProjectsDialog, setShowProjectsDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [currentProjectId, setCurrentProjectId] = useState(null);

  // Building data state
  const [buildingData, setBuildingData] = useState({
    dimensions: { width: 30, length: 40, height: 12 },
    floors: [{ name: 'الدور الأرضي', height: 4, area: 1200 }],
    rooms: [],
    structure: {
      columns: [],
      beams: [],
      slabs: [],
      walls: []
    }
  });

  // Quantities state
  const [quantities, setQuantities] = useState(null);
  const [activeSection, setActiveSection] = useState('upload');

  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    structural: true,
    architectural: false,
    electrical: false,
    plumbing: false,
    hvac: false,
    companyProducts: true,
    site: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // File upload handler - REAL API
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      try {
        // Real API upload
        const response = await uploadProjectFile(file, project.name || 'مشروع جديد');
        
        if (response.data.success) {
          const fileData = response.data.file;
          const fileInfo = {
            id: Date.now() + Math.random(),
            name: fileData.originalName,
            size: fileData.size,
            type: fileData.type,
            path: fileData.path,
            file: file,  // Keep original file for analysis
            status: 'uploaded',
            uploadedAt: new Date().toISOString()
          };

          setUploadedFiles(prev => [...prev, fileInfo]);
          toast.success(`تم رفع ${file.name}`);
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`فشل رفع ${file.name}`);
      }
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Remove file
  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  // Analyze files with AI - REAL API
  const handleAnalyze = async () => {
    if (uploadedFiles.length === 0) {
      toast.error('يرجى رفع ملفات أولاً');
      return;
    }

    setAnalyzing(true);
    try {
      // Get the first file to analyze
      const fileToAnalyze = uploadedFiles[0];
      
      // Re-upload and analyze if we have the file
      if (fileToAnalyze.file) {
        const response = await analyzeAndCalculate(fileToAnalyze.file, project.name || 'مشروع جديد');
        
        if (response.data.success) {
          const extracted = response.data.extracted_dimensions;
          const analysis = response.data.file_analysis;
          
          // Update building data with extracted dimensions
          const floorArea = extracted.width * extracted.length;
          const floorCount = extracted.floors || 4;
          const floorHeight = extracted.height / floorCount;
          
          const floorNames = ['البدروم', 'الدور الأرضي', 'الدور الأول', 'الدور الثاني', 
                            'الدور الثالث', 'الدور الرابع', 'الدور الخامس'];
          
          const floors = [];
          for (let i = 0; i < floorCount; i++) {
            floors.push({
              name: floorNames[i] || `الدور ${i}`,
              height: floorHeight,
              area: floorArea
            });
          }
          
          setBuildingData(prev => ({
            ...prev,
            dimensions: { 
              width: extracted.width, 
              length: extracted.length, 
              height: extracted.height 
            },
            floors: floors
          }));
          
          // Also set quantities if returned
          if (response.data.quantities) {
            const apiResult = response.data.quantities;
            const transformedQuantities = {
              projectInfo: apiResult.projectInfo,
              ...apiResult.quantities
            };
            setQuantities(transformedQuantities);
          }
          
          toast.success(`تم تحليل الملف واستخراج الأبعاد: ${extracted.width}×${extracted.length} متر`);
        }
      } else {
        // Fallback: simulated analysis
        setBuildingData(prev => ({
          ...prev,
          dimensions: { width: 35, length: 45, height: 16 },
          floors: [
            { name: 'البدروم', height: 3.5, area: 1575 },
            { name: 'الدور الأرضي', height: 4, area: 1575 },
            { name: 'الدور الأول', height: 3.5, area: 1575 },
            { name: 'الدور الثاني', height: 3.5, area: 1575 }
          ]
        }));
        toast.success('تم تحليل المخططات بنجاح');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('فشل في تحليل المخططات');
    } finally {
      setAnalyzing(false);
    }
  };

  // Calculate quantities - REAL API
  const handleCalculate = async () => {
    setCalculating(true);
    try {
      // Prepare data for API
      const requestData = {
        buildingData: {
          dimensions: buildingData.dimensions,
          floors: buildingData.floors
        },
        projectInfo: {
          name: project.name,
          location: project.location,
          client: project.client,
          buildingType: project.buildingType
        },
        selectedCategories: null  // Calculate all
      };
      
      const response = await calculateQuantities(requestData.buildingData, requestData.projectInfo, requestData.selectedCategories);
      
      if (response.data.success) {
        // Transform API response to match frontend format
        const apiResult = response.data.data;
        const transformedQuantities = {
          projectInfo: apiResult.projectInfo,
          ...apiResult.quantities
        };
        setQuantities(transformedQuantities);
        setActiveSection('results');
        toast.success('تم حساب الكميات بنجاح');
      }
    } catch (error) {
      console.error('Calculate error:', error);
      // Fallback to local calculation if API fails
      const calculatedQuantities = calculateAllQuantities(buildingData, project);
      setQuantities(calculatedQuantities);
      setActiveSection('results');
      toast.success('تم حساب الكميات بنجاح');
    } finally {
      setCalculating(false);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    if (!quantities || Object.keys(quantities).length === 0) {
      toast.error('يرجى حساب الكميات أولاً');
      return;
    }
    
    setExporting(true);
    try {
      const requestData = {
        buildingData: {
          dimensions: buildingData.dimensions,
          floors: buildingData.floors
        },
        projectInfo: {
          name: project.name,
          location: project.location,
          client: project.client,
          buildingType: project.buildingType
        },
        selectedCategories: null
      };
      
      const response = await exportQuantityExcel(requestData.buildingData, requestData.projectInfo, requestData.selectedCategories);
      
      if (response.data.success) {
        // Download the file
        const downloadUrl = getQuantityDownloadUrl(response.data.download_url.split('/').pop());
        window.open(downloadUrl, '_blank');
        toast.success('تم إنشاء ملف Excel بنجاح');
      }
    } catch (error) {
      console.error('Export Excel error:', error);
      toast.error('فشل في تصدير Excel');
    } finally {
      setExporting(false);
    }
  };

  // Export to PDF
  const handleExportPdf = async () => {
    if (!quantities || Object.keys(quantities).length === 0) {
      toast.error('يرجى حساب الكميات أولاً');
      return;
    }
    
    setExporting(true);
    try {
      const requestData = {
        buildingData: {
          dimensions: buildingData.dimensions,
          floors: buildingData.floors
        },
        projectInfo: {
          name: project.name,
          location: project.location,
          client: project.client,
          buildingType: project.buildingType
        },
        selectedCategories: null
      };
      
      const response = await exportQuantityPdf(requestData.buildingData, requestData.projectInfo, requestData.selectedCategories);
      
      if (response.data.success) {
        const downloadUrl = getQuantityDownloadUrl(response.data.download_url.split('/').pop());
        window.open(downloadUrl, '_blank');
        toast.success('تم إنشاء ملف PDF بنجاح');
      }
    } catch (error) {
      console.error('Export PDF error:', error);
      toast.error('فشل في تصدير PDF');
    } finally {
      setExporting(false);
    }
  };

  // Generate CAD Drawings
  const handleGenerateCad = async () => {
    setGeneratingCad(true);
    try {
      const requestData = {
        buildingData: {
          dimensions: buildingData.dimensions,
          floors: buildingData.floors
        },
        projectInfo: {
          name: project.name,
          location: project.location,
          client: project.client,
          buildingType: project.buildingType
        },
        selectedCategories: null
      };
      
      const response = await generateCadDrawings(requestData.buildingData, requestData.projectInfo, requestData.selectedCategories);
      
      if (response.data.success) {
        toast.success(`تم إنشاء ${response.data.count} رسم AutoCAD بنجاح`);
        
        // Show download links
        const drawings = response.data.drawings;
        Object.entries(drawings).forEach(([key, url]) => {
          if (!key.includes('error') && url.startsWith('/api/')) {
            console.log(`${key}: ${url}`);
          }
        });
        
        // Download first drawing (structural plan)
        if (drawings.structural_plan) {
          const downloadUrl = getQuantityDownloadUrl(drawings.structural_plan.split('/').pop());
          window.open(downloadUrl, '_blank');
        }
      }
    } catch (error) {
      console.error('Generate CAD error:', error);
      toast.error('فشل في إنشاء رسومات AutoCAD');
    } finally {
      setGeneratingCad(false);
    }
  };

  // Load saved projects
  const loadSavedProjects = async () => {
    setLoadingProjects(true);
    try {
      const response = await listQuantityProjects();
      if (response.data.success) {
        setSavedProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('فشل في تحميل المشاريع');
    } finally {
      setLoadingProjects(false);
    }
  };

  // Save current project
  const handleSaveProject = async () => {
    if (!projectName.trim()) {
      toast.error('يرجى إدخال اسم المشروع');
      return;
    }
    
    setSavingProject(true);
    try {
      const response = await saveCurrentProject(
        projectName,
        projectDescription,
        'user',  // TODO: Get actual user ID from auth
        buildingData.dimensions,
        buildingData.floors,
        {
          name: project.name || projectName,
          location: project.location,
          client: project.client,
          buildingType: project.buildingType
        },
        quantities || {}
      );
      
      if (response.data.success) {
        toast.success('تم حفظ المشروع بنجاح');
        setCurrentProjectId(response.data.project.id);
        setShowSaveDialog(false);
        setProjectName('');
        setProjectDescription('');
        // Update project name in state
        setProject(prev => ({ ...prev, name: projectName }));
      }
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error('فشل في حفظ المشروع');
    } finally {
      setSavingProject(false);
    }
  };

  // Load a saved project
  const handleLoadProject = async (projectId) => {
    try {
      const response = await getQuantityProject(projectId);
      if (response.data.success) {
        const proj = response.data.project;
        
        // Load project data
        setProject({
          name: proj.project_info?.name || proj.name || '',
          location: proj.project_info?.location || '',
          client: proj.project_info?.client || '',
          floors: proj.floors?.length || 1,
          totalArea: proj.total_area || 0,
          buildingType: proj.project_info?.buildingType || 'commercial'
        });
        
        setBuildingData(prev => ({
          ...prev,
          dimensions: proj.dimensions || { width: 30, length: 40, height: 12 },
          floors: proj.floors || [{ name: 'الدور الأرضي', height: 4, area: 1200 }]
        }));
        
        if (proj.quantities) {
          setQuantities(proj.quantities);
        }
        
        setCurrentProjectId(projectId);
        setShowProjectsDialog(false);
        toast.success(`تم تحميل المشروع: ${proj.name}`);
      }
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('فشل في تحميل المشروع');
    }
  };

  // Delete a project
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المشروع؟')) return;
    
    try {
      const response = await deleteQuantityProject(projectId);
      if (response.data.success) {
        toast.success('تم حذف المشروع');
        setSavedProjects(prev => prev.filter(p => p.id !== projectId));
        if (currentProjectId === projectId) {
          setCurrentProjectId(null);
        }
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('فشل في حذف المشروع');
    }
  };

  // Calculate all quantities
  const calculateAllQuantities = (data, projectInfo) => {
    const totalArea = data.floors.reduce((sum, f) => sum + f.area, 0);
    const totalHeight = data.floors.reduce((sum, f) => sum + f.height, 0);
    const perimeter = 2 * (data.dimensions.width + data.dimensions.length);

    return {
      projectInfo: {
        name: projectInfo.name || 'مشروع جديد',
        location: projectInfo.location,
        totalArea,
        floors: data.floors.length,
        calculatedAt: new Date().toISOString()
      },

      // 1. Structural
      structural: {
        concrete: {
          foundations: { volume: totalArea * 0.5 / 1000, unit: 'م³' },
          columns: { count: Math.ceil(totalArea / 36), volume: Math.ceil(totalArea / 36) * 0.4 * 0.4 * totalHeight, unit: 'م³' },
          beams: { volume: perimeter * data.floors.length * 0.3 * 0.5, unit: 'م³' },
          slabs: { area: totalArea, volume: totalArea * 0.2, unit: 'م³' },
          totalVolume: 0
        },
        reinforcement: {
          weight: totalArea * 0.12, // 120 kg/m² تقريباً
          unit: 'طن'
        },
        masonry: {
          externalWalls: { area: perimeter * totalHeight, blocks: perimeter * totalHeight * 12.5, unit: 'م²' },
          internalWalls: { area: totalArea * 0.3, blocks: totalArea * 0.3 * 12.5, unit: 'م²' }
        }
      },

      // 2. Architectural
      architectural: {
        flooring: {
          ceramic: { area: totalArea * 0.4, tiles: Math.ceil(totalArea * 0.4 / 0.36), unit: 'م²' },
          porcelain: { area: totalArea * 0.3, tiles: Math.ceil(totalArea * 0.3 / 0.36), unit: 'م²' },
          marble: { area: totalArea * 0.1, unit: 'م²' },
          vinyl: { area: totalArea * 0.2, unit: 'م²' }
        },
        walls: {
          plastering: { area: (perimeter * totalHeight) + (totalArea * 0.3 * 2), unit: 'م²' },
          painting: { area: totalArea * 2.5, liters: totalArea * 2.5 * 0.15, unit: 'م²' },
          tiles: { area: totalArea * 0.15, unit: 'م²' }
        },
        ceilings: {
          suspended: { area: totalArea * 0.6, panels: Math.ceil(totalArea * 0.6 / 0.36), unit: 'م²' },
          gypsum: { area: totalArea * 0.4, unit: 'م²' }
        },
        doors: {
          wooden: { count: Math.ceil(totalArea / 25), unit: 'باب' },
          aluminum: { count: Math.ceil(totalArea / 100), unit: 'باب' },
          steel: { count: Math.ceil(data.floors.length * 2), unit: 'باب' }
        },
        windows: {
          count: Math.ceil(perimeter / 4),
          area: Math.ceil(perimeter / 4) * 2,
          unit: 'م²'
        }
      },

      // 3. Electrical
      electrical: {
        conduits: { length: totalArea * 2, unit: 'متر' },
        wires: { length: totalArea * 8, unit: 'متر' },
        outlets: { count: Math.ceil(totalArea / 4), unit: 'قطعة' },
        switches: { count: Math.ceil(totalArea / 8), unit: 'قطعة' },
        lighting: { fixtures: Math.ceil(totalArea / 6), unit: 'قطعة' },
        panels: { main: 1, sub: data.floors.length, unit: 'لوحة' }
      },

      // 4. Plumbing
      plumbing: {
        waterPipes: { length: totalArea * 0.8, unit: 'متر' },
        drainPipes: { length: totalArea * 0.5, unit: 'متر' },
        fixtures: {
          toilets: Math.ceil(totalArea / 50),
          sinks: Math.ceil(totalArea / 30),
          showers: Math.ceil(totalArea / 100)
        },
        waterHeaters: Math.ceil(data.floors.length / 2)
      },

      // 5. HVAC
      hvac: {
        ducts: { length: totalArea * 0.3, unit: 'متر' },
        diffusers: { count: Math.ceil(totalArea / 15), unit: 'قطعة' },
        units: {
          split: Math.ceil(totalArea / 25),
          central: Math.ceil(totalArea / 500)
        },
        insulation: { area: totalArea * 0.3, unit: 'م²' }
      },

      // 6. Company Products (التسعة)
      companyProducts: {
        fireStopSealant: calculateFireStop(data, totalArea),
        expansionJointCover: calculateExpansionJoints(data, perimeter),
        raisedFloor: calculateRaisedFloor(data, totalArea),
        rubberGuards: calculateRubberGuards(data, totalArea, perimeter),
        aluminumMat: calculateAluminumMat(data),
        pvcHandrail: calculatePVCHandrail(data, totalHeight),
        toiletPartitions: calculateToiletPartitions(data, totalArea),
        stairNosing: calculateStairNosing(data),
        steelDoors: calculateSteelDoors(data)
      },

      // 7. Site Works
      site: {
        excavation: { volume: totalArea * 0.5 * 2, unit: 'م³' },
        backfill: { volume: totalArea * 0.5 * 1.5, unit: 'م³' },
        asphalt: { area: totalArea * 0.3, unit: 'م²' },
        interlock: { area: totalArea * 0.2, pieces: totalArea * 0.2 * 44, unit: 'م²' },
        landscaping: { area: totalArea * 0.15, unit: 'م²' }
      }
    };
  };

  // Company Products Calculations
  const calculateFireStop = (data, totalArea) => {
    const penetrations = Math.ceil(totalArea / 20); // تقدير عدد الفتحات
    const volumePerPenetration = 0.5; // لتر لكل فتحة تقريباً
    const totalVolume = penetrations * volumePerPenetration;
    const cartridges = Math.ceil(totalVolume / 0.3); // 300 مل لكل خرطوشة
    
    return {
      penetrations,
      volumeLiters: totalVolume.toFixed(2),
      cartridges,
      weightKg: (totalVolume * 1.22).toFixed(2),
      items: [
        { type: 'Pipe Penetrations', count: Math.ceil(penetrations * 0.5), volume: (penetrations * 0.5 * 0.4).toFixed(2) },
        { type: 'Cable Penetrations', count: Math.ceil(penetrations * 0.3), volume: (penetrations * 0.3 * 0.3).toFixed(2) },
        { type: 'Duct Penetrations', count: Math.ceil(penetrations * 0.2), volume: (penetrations * 0.2 * 1.0).toFixed(2) }
      ]
    };
  };

  const calculateExpansionJoints = (data, perimeter) => {
    const floorJoints = perimeter * data.floors.length * 0.1;
    const wallJoints = data.dimensions.height * 4 * data.floors.length;
    
    return {
      floor: { length: floorJoints.toFixed(2), unit: 'متر' },
      wall: { length: wallJoints.toFixed(2), unit: 'متر' },
      ceiling: { length: (floorJoints * 0.5).toFixed(2), unit: 'متر' },
      total: (floorJoints + wallJoints + floorJoints * 0.5).toFixed(2)
    };
  };

  const calculateRaisedFloor = (data, totalArea) => {
    const raisedFloorArea = totalArea * 0.15; // 15% من المساحة للأرضيات المرتفعة
    const panelSize = 0.36; // 600x600mm
    
    return {
      area: raisedFloorArea.toFixed(2),
      panels: Math.ceil(raisedFloorArea / panelSize),
      pedestals: Math.ceil(raisedFloorArea / panelSize) * 4,
      stringers: Math.ceil(raisedFloorArea / panelSize) * 4,
      unit: 'م²'
    };
  };

  const calculateRubberGuards = (data, totalArea, perimeter) => {
    return {
      cornerGuards: Math.ceil(totalArea / 50) * 4, // 4 زوايا لكل 50 م²
      wallGuards: Math.ceil(perimeter * data.floors.length * 0.3), // 30% من المحيط
      wheelStoppers: Math.ceil(totalArea * 0.02), // موقف لكل 50 م²
      speedHumps: Math.ceil(perimeter / 50), // مطب كل 50 متر
      unit: 'قطعة'
    };
  };

  const calculateAluminumMat = (data) => {
    const entrances = data.floors.length + 2;
    const matArea = entrances * 1.5; // 1.5 م² لكل مدخل
    
    return {
      entrances,
      area: matArea.toFixed(2),
      sheets: Math.ceil(matArea / 0.5),
      unit: 'م²'
    };
  };

  const calculatePVCHandrail = (data, totalHeight) => {
    const stairRuns = data.floors.length * 2; // درجين لكل دور
    const lengthPerRun = totalHeight / data.floors.length * 1.5; // طول الدرابزين
    
    return {
      totalLength: (stairRuns * lengthPerRun).toFixed(2),
      fittings: Math.ceil(stairRuns * lengthPerRun / 3), // قطعة ربط كل 3 متر
      brackets: Math.ceil(stairRuns * lengthPerRun / 1), // كتيفة كل متر
      unit: 'متر'
    };
  };

  const calculateToiletPartitions = (data, totalArea) => {
    const bathroomArea = totalArea * 0.05; // 5% من المساحة للحمامات
    const stallsCount = Math.ceil(bathroomArea / 2.5); // كابينة لكل 2.5 م²
    const adaStalls = Math.ceil(stallsCount * 0.1); // 10% ADA
    
    return {
      totalStalls: stallsCount,
      standardStalls: stallsCount - adaStalls,
      adaStalls,
      doors: {
        standard: { count: stallsCount - adaStalls, width: 610, height: 1473 },
        ada: { count: adaStalls, width: 813, height: 1473 }
      },
      panels: stallsCount * 2,
      pilasters: stallsCount,
      accessories: {
        locks: stallsCount,
        hinges: stallsCount * 3,
        knobs: stallsCount,
        legs: stallsCount * 4,
        coatHooks: stallsCount,
        doorBumpers: stallsCount
      }
    };
  };

  const calculateStairNosing = (data) => {
    const stepsPerFloor = Math.ceil(data.dimensions.height / data.floors.length / 0.17); // 17cm لكل درجة
    const totalSteps = stepsPerFloor * data.floors.length * 2; // درجين لكل دور
    const stepWidth = 1.2; // عرض الدرج بالمتر
    
    return {
      totalSteps,
      totalLength: (totalSteps * stepWidth).toFixed(2),
      pieces: totalSteps,
      unit: 'قطعة'
    };
  };

  const calculateSteelDoors = (data) => {
    const fireDoors = data.floors.length * 2; // بابين حريق لكل دور
    const serviceDoors = Math.ceil(data.floors.length * 1.5);
    
    return {
      fireDoors: { count: fireDoors, sizes: ['900x2100', '1200x2100'] },
      serviceDoors: { count: serviceDoors, sizes: ['900x2100'] },
      total: fireDoors + serviceDoors,
      unit: 'باب'
    };
  };

  // Export BOQ
  const handleExportBOQ = async (format) => {
    if (!quantities) {
      toast.error('يرجى حساب الكميات أولاً');
      return;
    }

    toast.success(`جاري تصدير BOQ بصيغة ${format}...`);
    // TODO: Implement export functionality
  };

  // Generate Drawing
  const handleGenerateDrawing = async (type) => {
    if (!quantities) {
      toast.error('يرجى حساب الكميات أولاً');
      return;
    }

    toast.success(`جاري توليد رسم ${type}...`);
    // TODO: Implement drawing generation
  };

  // Get file icon
  const getFileIcon = (type) => {
    if (type.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    if (type.includes('image')) return <Image className="h-5 w-5 text-blue-500" />;
    if (type.includes('spreadsheet') || type.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
            حساب الكميات
            {currentProjectId && <span className="text-sm text-muted-foreground mr-2">(محفوظ)</span>}
          </h1>
          <p className="text-muted-foreground mt-1">Quantity Calculator</p>
        </div>
        <div className="flex gap-2">
          {/* Project Management Buttons */}
          <Button 
            variant="outline"
            onClick={() => {
              loadSavedProjects();
              setShowProjectsDialog(true);
            }}
          >
            <FolderOpen className="h-4 w-4 ml-2" />
            المشاريع المحفوظة
          </Button>
          <Button 
            variant="outline"
            onClick={() => setShowSaveDialog(true)}
          >
            <Save className="h-4 w-4 ml-2" />
            حفظ المشروع
          </Button>
          
          {quantities && (
            <>
              <Button 
                variant="outline" 
                onClick={handleExportExcel}
                disabled={exporting}
              >
                <FileSpreadsheet className="h-4 w-4 ml-2" />
                {exporting ? 'جاري التصدير...' : 'تصدير Excel'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleExportPdf}
                disabled={exporting}
              >
                <FileText className="h-4 w-4 ml-2" />
                {exporting ? 'جاري التصدير...' : 'تصدير PDF'}
              </Button>
              <Button 
                onClick={handleGenerateCad}
                disabled={generatingCad}
              >
                <Layers className="h-4 w-4 ml-2" />
                {generatingCad ? 'جاري التوليد...' : 'توليد AutoCAD'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeSection} onValueChange={setActiveSection}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="upload" className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            رفع الملفات
          </TabsTrigger>
          <TabsTrigger value="input" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            بيانات المبنى
          </TabsTrigger>
          <TabsTrigger value="calculate" className="flex items-center gap-2">
            <Calculator className="h-4 w-4" />
            حساب الكميات
          </TabsTrigger>
          <TabsTrigger value="results" className="flex items-center gap-2" disabled={!quantities}>
            <FileSpreadsheet className="h-4 w-4" />
            النتائج
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                رفع ملفات المشروع
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Project Info */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>اسم المشروع</Label>
                  <Input
                    value={project.name}
                    onChange={(e) => setProject(p => ({ ...p, name: e.target.value }))}
                    placeholder="مثال: مبنى الإدارة الرئيسي"
                  />
                </div>
                <div>
                  <Label>الموقع</Label>
                  <Input
                    value={project.location}
                    onChange={(e) => setProject(p => ({ ...p, location: e.target.value }))}
                    placeholder="مثال: الرياض"
                  />
                </div>
                <div>
                  <Label>العميل</Label>
                  <Input
                    value={project.client}
                    onChange={(e) => setProject(p => ({ ...p, client: e.target.value }))}
                    placeholder="اسم العميل"
                  />
                </div>
              </div>

              {/* Upload Area */}
              <div
                className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.xlsx,.xls"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">اضغط أو اسحب الملفات هنا</p>
                <p className="text-sm text-muted-foreground mt-2">
                  PDF, DWG, DXF, صور, Excel
                </p>
              </div>

              {/* Uploaded Files */}
              {uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label>الملفات المرفوعة ({uploadedFiles.length})</Label>
                  <div className="space-y-2">
                    {uploadedFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          {getFileIcon(file.type)}
                          <div>
                            <p className="font-medium">{file.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatFileSize(file.size)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-green-600">
                            <CheckCircle className="h-3 w-3 ml-1" />
                            تم الرفع
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(file.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Analyze Button */}
              {uploadedFiles.length > 0 && (
                <Button
                  className="w-full"
                  onClick={handleAnalyze}
                  disabled={analyzing}
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                      جاري التحليل...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 ml-2" />
                      تحليل المخططات بالذكاء الاصطناعي
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Building Data Tab */}
        <TabsContent value="input" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                بيانات المبنى
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Building Dimensions */}
              <div>
                <Label className="text-lg font-semibold mb-3 block">أبعاد المبنى</Label>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>العرض (متر)</Label>
                    <Input
                      type="number"
                      value={buildingData.dimensions.width}
                      onChange={(e) => setBuildingData(d => ({
                        ...d,
                        dimensions: { ...d.dimensions, width: parseFloat(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                  <div>
                    <Label>الطول (متر)</Label>
                    <Input
                      type="number"
                      value={buildingData.dimensions.length}
                      onChange={(e) => setBuildingData(d => ({
                        ...d,
                        dimensions: { ...d.dimensions, length: parseFloat(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                  <div>
                    <Label>الارتفاع الكلي (متر)</Label>
                    <Input
                      type="number"
                      value={buildingData.dimensions.height}
                      onChange={(e) => setBuildingData(d => ({
                        ...d,
                        dimensions: { ...d.dimensions, height: parseFloat(e.target.value) || 0 }
                      }))}
                    />
                  </div>
                  <div>
                    <Label>نوع المبنى</Label>
                    <select
                      className="w-full h-10 rounded-md border border-input bg-background px-3"
                      value={project.buildingType}
                      onChange={(e) => setProject(p => ({ ...p, buildingType: e.target.value }))}
                    >
                      <option value="commercial">تجاري</option>
                      <option value="residential">سكني</option>
                      <option value="industrial">صناعي</option>
                      <option value="educational">تعليمي</option>
                      <option value="healthcare">صحي</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Floors */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-lg font-semibold">الأدوار</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBuildingData(d => ({
                      ...d,
                      floors: [...d.floors, { name: `دور ${d.floors.length + 1}`, height: 3.5, area: d.dimensions.width * d.dimensions.length }]
                    }))}
                  >
                    <Plus className="h-4 w-4 ml-1" />
                    إضافة دور
                  </Button>
                </div>
                <div className="space-y-2">
                  {buildingData.floors.map((floor, index) => (
                    <div key={index} className="grid grid-cols-4 gap-4 p-3 bg-muted rounded-lg">
                      <div>
                        <Label>اسم الدور</Label>
                        <Input
                          value={floor.name}
                          onChange={(e) => {
                            const newFloors = [...buildingData.floors];
                            newFloors[index].name = e.target.value;
                            setBuildingData(d => ({ ...d, floors: newFloors }));
                          }}
                        />
                      </div>
                      <div>
                        <Label>الارتفاع (متر)</Label>
                        <Input
                          type="number"
                          value={floor.height}
                          onChange={(e) => {
                            const newFloors = [...buildingData.floors];
                            newFloors[index].height = parseFloat(e.target.value) || 0;
                            setBuildingData(d => ({ ...d, floors: newFloors }));
                          }}
                        />
                      </div>
                      <div>
                        <Label>المساحة (م²)</Label>
                        <Input
                          type="number"
                          value={floor.area}
                          onChange={(e) => {
                            const newFloors = [...buildingData.floors];
                            newFloors[index].area = parseFloat(e.target.value) || 0;
                            setBuildingData(d => ({ ...d, floors: newFloors }));
                          }}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500"
                          onClick={() => {
                            if (buildingData.floors.length > 1) {
                              setBuildingData(d => ({
                                ...d,
                                floors: d.floors.filter((_, i) => i !== index)
                              }));
                            }
                          }}
                          disabled={buildingData.floors.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-primary/5 rounded-lg">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">المساحة الإجمالية</p>
                  <p className="text-2xl font-bold">
                    {buildingData.floors.reduce((sum, f) => sum + f.area, 0).toLocaleString()} م²
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">عدد الأدوار</p>
                  <p className="text-2xl font-bold">{buildingData.floors.length}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">محيط المبنى</p>
                  <p className="text-2xl font-bold">
                    {(2 * (buildingData.dimensions.width + buildingData.dimensions.length)).toLocaleString()} متر
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calculate Tab */}
        <TabsContent value="calculate" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                حساب الكميات
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">المشروع:</p>
                  <p className="text-muted-foreground">{project.name || 'مشروع جديد'}</p>
                </div>
                <div>
                  <p className="font-medium">المساحة الإجمالية:</p>
                  <p className="text-muted-foreground">
                    {buildingData.floors.reduce((sum, f) => sum + f.area, 0).toLocaleString()} م²
                  </p>
                </div>
                <div>
                  <p className="font-medium">عدد الأدوار:</p>
                  <p className="text-muted-foreground">{buildingData.floors.length}</p>
                </div>
                <div>
                  <p className="font-medium">نوع المبنى:</p>
                  <p className="text-muted-foreground">
                    {project.buildingType === 'commercial' ? 'تجاري' :
                     project.buildingType === 'residential' ? 'سكني' :
                     project.buildingType === 'industrial' ? 'صناعي' :
                     project.buildingType === 'educational' ? 'تعليمي' : 'صحي'}
                  </p>
                </div>
              </div>

              <Button
                className="w-full h-14 text-lg"
                onClick={handleCalculate}
                disabled={calculating}
              >
                {calculating ? (
                  <>
                    <Loader2 className="h-5 w-5 ml-2 animate-spin" />
                    جاري حساب الكميات...
                  </>
                ) : (
                  <>
                    <Calculator className="h-5 w-5 ml-2" />
                    حساب جميع الكميات
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-4">
          {quantities && (
            <>
              {/* Summary Card */}
              <Card>
                <CardHeader>
                  <CardTitle>ملخص المشروع</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <Building2 className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                      <p className="text-sm text-muted-foreground">المساحة</p>
                      <p className="text-xl font-bold">{quantities.projectInfo.totalArea.toLocaleString()} م²</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <Layers className="h-8 w-8 mx-auto text-green-600 mb-2" />
                      <p className="text-sm text-muted-foreground">الأدوار</p>
                      <p className="text-xl font-bold">{quantities.projectInfo.floors}</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <Calculator className="h-8 w-8 mx-auto text-orange-600 mb-2" />
                      <p className="text-sm text-muted-foreground">الخرسانة</p>
                      <p className="text-xl font-bold">{Math.round(quantities.structural.concrete.slabs.volume)} م³</p>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <Shield className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                      <p className="text-sm text-muted-foreground">منتجات الشركة</p>
                      <p className="text-xl font-bold">9 أقسام</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Company Products - Highlighted */}
              <Card className="border-primary">
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => toggleSection('companyProducts')}
                >
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-primary" />
                      منتجات الشركة (التسعة)
                      <Badge className="bg-primary">الأولوية</Badge>
                    </div>
                    {expandedSections.companyProducts ? <ChevronUp /> : <ChevronDown />}
                  </CardTitle>
                </CardHeader>
                {expandedSections.companyProducts && (
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      {/* Fire Stop */}
                      <div className="p-4 bg-red-50 rounded-lg">
                        <h4 className="font-semibold text-red-700 mb-2">🔥 Fire Stop Sealant</h4>
                        <div className="space-y-1 text-sm">
                          <p>عدد الفتحات: {quantities.companyProducts.fireStopSealant.penetrations}</p>
                          <p>الحجم: {quantities.companyProducts.fireStopSealant.volumeLiters} لتر</p>
                          <p className="font-bold">الخراطيش: {quantities.companyProducts.fireStopSealant.cartridges}</p>
                          <p>الوزن: {quantities.companyProducts.fireStopSealant.weightKg} كجم</p>
                        </div>
                      </div>

                      {/* Expansion Joints */}
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-semibold text-blue-700 mb-2">📏 Expansion Joint Cover</h4>
                        <div className="space-y-1 text-sm">
                          <p>أرضي: {quantities.companyProducts.expansionJointCover.floor.length} متر</p>
                          <p>جداري: {quantities.companyProducts.expansionJointCover.wall.length} متر</p>
                          <p>سقفي: {quantities.companyProducts.expansionJointCover.ceiling.length} متر</p>
                          <p className="font-bold">الإجمالي: {quantities.companyProducts.expansionJointCover.total} متر</p>
                        </div>
                      </div>

                      {/* Raised Floor */}
                      <div className="p-4 bg-green-50 rounded-lg">
                        <h4 className="font-semibold text-green-700 mb-2">🏗️ Raised Floor System</h4>
                        <div className="space-y-1 text-sm">
                          <p>المساحة: {quantities.companyProducts.raisedFloor.area} م²</p>
                          <p className="font-bold">البلاطات: {quantities.companyProducts.raisedFloor.panels}</p>
                          <p>القواعد: {quantities.companyProducts.raisedFloor.pedestals}</p>
                          <p>الموصلات: {quantities.companyProducts.raisedFloor.stringers}</p>
                        </div>
                      </div>

                      {/* Rubber Guards */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-gray-700 mb-2">🛡️ Rubber Guards</h4>
                        <div className="space-y-1 text-sm">
                          <p>حافظة زوايا: {quantities.companyProducts.rubberGuards.cornerGuards}</p>
                          <p>حافظة جدران: {quantities.companyProducts.rubberGuards.wallGuards}</p>
                          <p>مصد عجلات: {quantities.companyProducts.rubberGuards.wheelStoppers}</p>
                          <p>مطبات: {quantities.companyProducts.rubberGuards.speedHumps}</p>
                        </div>
                      </div>

                      {/* Aluminum Mat */}
                      <div className="p-4 bg-yellow-50 rounded-lg">
                        <h4 className="font-semibold text-yellow-700 mb-2">🚪 Aluminum Floor Mat</h4>
                        <div className="space-y-1 text-sm">
                          <p>عدد المداخل: {quantities.companyProducts.aluminumMat.entrances}</p>
                          <p className="font-bold">المساحة: {quantities.companyProducts.aluminumMat.area} م²</p>
                          <p>الألواح: {quantities.companyProducts.aluminumMat.sheets}</p>
                        </div>
                      </div>

                      {/* PVC Handrail */}
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <h4 className="font-semibold text-purple-700 mb-2">🪜 PVC Handrail</h4>
                        <div className="space-y-1 text-sm">
                          <p className="font-bold">الطول: {quantities.companyProducts.pvcHandrail.totalLength} متر</p>
                          <p>قطع الربط: {quantities.companyProducts.pvcHandrail.fittings}</p>
                          <p>الكتائف: {quantities.companyProducts.pvcHandrail.brackets}</p>
                        </div>
                      </div>

                      {/* Toilet Partitions */}
                      <div className="p-4 bg-cyan-50 rounded-lg">
                        <h4 className="font-semibold text-cyan-700 mb-2">🚻 Toilet Partitions</h4>
                        <div className="space-y-1 text-sm">
                          <p className="font-bold">الكبائن: {quantities.companyProducts.toiletPartitions.totalStalls}</p>
                          <p>عادية: {quantities.companyProducts.toiletPartitions.standardStalls} | ADA: {quantities.companyProducts.toiletPartitions.adaStalls}</p>
                          <p>الأقفال: {quantities.companyProducts.toiletPartitions.accessories.locks}</p>
                          <p>المفصلات: {quantities.companyProducts.toiletPartitions.accessories.hinges}</p>
                        </div>
                      </div>

                      {/* Stair Nosing */}
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <h4 className="font-semibold text-orange-700 mb-2">🪜 Stair Nosing</h4>
                        <div className="space-y-1 text-sm">
                          <p className="font-bold">عدد الدرجات: {quantities.companyProducts.stairNosing.totalSteps}</p>
                          <p>الطول الإجمالي: {quantities.companyProducts.stairNosing.totalLength} متر</p>
                          <p>القطع: {quantities.companyProducts.stairNosing.pieces}</p>
                        </div>
                      </div>

                      {/* Steel Doors */}
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2">🚪 Steel Doors</h4>
                        <div className="space-y-1 text-sm">
                          <p>أبواب حريق: {quantities.companyProducts.steelDoors.fireDoors.count}</p>
                          <p>أبواب خدمة: {quantities.companyProducts.steelDoors.serviceDoors.count}</p>
                          <p className="font-bold">الإجمالي: {quantities.companyProducts.steelDoors.total} باب</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Structural Section */}
              <Card>
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => toggleSection('structural')}
                >
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      الأعمال الإنشائية
                    </div>
                    {expandedSections.structural ? <ChevronUp /> : <ChevronDown />}
                  </CardTitle>
                </CardHeader>
                {expandedSections.structural && (
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">خرسانة الأعمدة</p>
                        <p className="text-lg font-bold">{quantities.structural.concrete.columns.volume.toFixed(1)} م³</p>
                        <p className="text-xs">عدد: {quantities.structural.concrete.columns.count}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">خرسانة الكمرات</p>
                        <p className="text-lg font-bold">{quantities.structural.concrete.beams.volume.toFixed(1)} م³</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">خرسانة البلاطات</p>
                        <p className="text-lg font-bold">{quantities.structural.concrete.slabs.volume.toFixed(1)} م³</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">حديد التسليح</p>
                        <p className="text-lg font-bold">{quantities.structural.reinforcement.weight.toFixed(1)} طن</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">جدران خارجية</p>
                        <p className="text-lg font-bold">{quantities.structural.masonry.externalWalls.area.toFixed(0)} م²</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">جدران داخلية</p>
                        <p className="text-lg font-bold">{quantities.structural.masonry.internalWalls.area.toFixed(0)} م²</p>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Other Sections... */}
              <Card>
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => toggleSection('electrical')}
                >
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="h-5 w-5" />
                      أعمال الكهرباء
                    </div>
                    {expandedSections.electrical ? <ChevronUp /> : <ChevronDown />}
                  </CardTitle>
                </CardHeader>
                {expandedSections.electrical && (
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">المواسير</p>
                        <p className="text-lg font-bold">{quantities.electrical.conduits.length.toLocaleString()} متر</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">الأسلاك</p>
                        <p className="text-lg font-bold">{quantities.electrical.wires.length.toLocaleString()} متر</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">الأفياش</p>
                        <p className="text-lg font-bold">{quantities.electrical.outlets.count}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">المفاتيح</p>
                        <p className="text-lg font-bold">{quantities.electrical.switches.count}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">الإنارة</p>
                        <p className="text-lg font-bold">{quantities.electrical.lighting.fixtures}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">لوحات التوزيع</p>
                        <p className="text-lg font-bold">{quantities.electrical.panels.main + quantities.electrical.panels.sub}</p>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>

              <Card>
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => toggleSection('plumbing')}
                >
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Droplets className="h-5 w-5" />
                      أعمال السباكة
                    </div>
                    {expandedSections.plumbing ? <ChevronUp /> : <ChevronDown />}
                  </CardTitle>
                </CardHeader>
                {expandedSections.plumbing && (
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">مواسير المياه</p>
                        <p className="text-lg font-bold">{quantities.plumbing.waterPipes.length.toFixed(0)} متر</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">مواسير الصرف</p>
                        <p className="text-lg font-bold">{quantities.plumbing.drainPipes.length.toFixed(0)} متر</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">المراحيض</p>
                        <p className="text-lg font-bold">{quantities.plumbing.fixtures.toilets}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">الأحواض</p>
                        <p className="text-lg font-bold">{quantities.plumbing.fixtures.sinks}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">الدشات</p>
                        <p className="text-lg font-bold">{quantities.plumbing.fixtures.showers}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg">
                        <p className="text-sm text-muted-foreground">السخانات</p>
                        <p className="text-lg font-bold">{quantities.plumbing.waterHeaters}</p>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Save Project Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>حفظ المشروع</span>
                <Button variant="ghost" size="icon" onClick={() => setShowSaveDialog(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>اسم المشروع *</Label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="مثال: مشروع مبنى تجاري - الرياض"
                />
              </div>
              <div>
                <Label>وصف المشروع (اختياري)</Label>
                <Input
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  placeholder="وصف مختصر للمشروع"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                  إلغاء
                </Button>
                <Button onClick={handleSaveProject} disabled={savingProject}>
                  {savingProject ? (
                    <>
                      <Loader2 className="h-4 w-4 ml-2 animate-spin" />
                      جاري الحفظ...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 ml-2" />
                      حفظ
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Saved Projects Dialog */}
      {showProjectsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>المشاريع المحفوظة</span>
                <Button variant="ghost" size="icon" onClick={() => setShowProjectsDialog(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-y-auto flex-1">
              {loadingProjects ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : savedProjects.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>لا توجد مشاريع محفوظة</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedProjects.map((proj) => (
                    <div
                      key={proj.id}
                      className={`p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors ${
                        currentProjectId === proj.id ? 'border-primary bg-primary/5' : ''
                      }`}
                      onClick={() => handleLoadProject(proj.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{proj.name}</h3>
                            <Badge variant={proj.status === 'active' ? 'default' : 'secondary'}>
                              {proj.status === 'active' ? 'نشط' : proj.status === 'draft' ? 'مسودة' : proj.status}
                            </Badge>
                          </div>
                          {proj.description && (
                            <p className="text-sm text-muted-foreground mt-1">{proj.description}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Ruler className="h-3 w-3" />
                              {proj.total_area?.toLocaleString() || 0} م²
                            </span>
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {proj.floors?.length || 0} أدوار
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatDate(proj.updated_at)}
                            </span>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(proj.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
